(() => {
  const selector = '[data-bh-carousel]';

  const setupCarousel = (carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const track = carousel.querySelector('[data-bh-carousel-track]');
    const previous = carousel.querySelector('[data-bh-carousel-previous]');
    const next = carousel.querySelector('[data-bh-carousel-next]');
    const progress = carousel.querySelector('[data-bh-carousel-progress]');

    if (!track || !previous || !next || !progress) return;
    carousel.dataset.carouselReady = 'true';

    const getStep = () => {
      const card = track.querySelector('.bh-product-card');
      if (!card) return track.clientWidth;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const maximum = Math.max(track.scrollWidth - track.clientWidth, 0);
      const position = Math.min(Math.max(track.scrollLeft, 0), maximum);
      const visibleRatio = Math.min(track.clientWidth / track.scrollWidth, 1);
      const travelRatio = maximum > 0 ? position / maximum : 0;

      previous.disabled = position <= 2;
      next.disabled = position >= maximum - 2;
      progress.style.transform = `translateX(${travelRatio * (1 - visibleRatio) * 100}%) scaleX(${visibleRatio})`;
    };

    const scroll = (direction) => {
      track.scrollBy({ left: getStep() * direction, behavior: 'smooth' });
    };

    previous.addEventListener('click', () => scroll(-1));
    next.addEventListener('click', () => scroll(1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') scroll(-1);
      if (event.key === 'ArrowRight') scroll(1);
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(track);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    update();
  };

  const setupAll = (root = document) => {
    root.querySelectorAll(selector).forEach(setupCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll());
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();

(() => {
  const packageButtonSelector = '[data-bh-package-add]';
  const packageRootSelector = '[data-bh-package-offers]';
  const cartUpdateEventName = 'shopify:cart:lines-update';

  const setButtonsDisabled = (root, disabled) => {
    root.querySelectorAll(packageButtonSelector).forEach((button) => {
      button.disabled = disabled || button.dataset.canAdd !== 'true';
    });
  };

  const refreshCartComponents = async () => {
    const components = Array.from(document.querySelectorAll('cart-items-component'));

    if (components.length) {
      await customElements.whenDefined('cart-items-component');
      await Promise.all(
        components.map((component) =>
          typeof component.fetchCartData === 'function' ? component.fetchCartData() : Promise.resolve()
        )
      );
    }
  };

  const getCart = async (routesRoot) => {
    const response = await fetch(`${routesRoot}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Varukorgen kunde inte uppdateras.');
    return response.json();
  };

  const addPackage = async (button, root, variantIds) => {
    const routesRoot = window.Shopify?.routes?.root || '/';
    const discountCode = button.dataset.discountCode;
    const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
    const status = root.querySelector('[data-bh-package-status]');

    setButtonsDisabled(root, true);
    button.classList.add('is-loading');
    if (status) status.textContent = `Lägger ${button.dataset.packageName} i varukorgen…`;

    const updatePromise = (async () => {
      const addResponse = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!addResponse.ok) {
        const error = await addResponse.json().catch(() => ({}));
        throw new Error(error.description || 'Paketet kunde inte läggas i varukorgen.');
      }

      if (discountCode) {
        const discountResponse = await fetch(`${routesRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ discount: discountCode }),
        });

        if (!discountResponse.ok) {
          const error = await discountResponse.json().catch(() => ({}));
          throw new Error(error.description || 'Paketets rabatt kunde inte aktiveras.');
        }
      }

      await refreshCartComponents();
      const cart = await getCart(routesRoot);

      return {
        cart,
        detail: {
          didError: false,
          itemCount: items.length,
          items: cart.items,
          source: 'belyr-package-offers',
          sourceId: button.dataset.packageName || 'package-offer',
        },
      };
    })();

    const cartEvent = new CustomEvent(cartUpdateEventName, { bubbles: true });
    Object.defineProperties(cartEvent, {
      action: { value: 'add' },
      context: { value: 'product' },
      lines: {
        value: variantIds.map((id) => ({ merchandiseId: id, quantity: 1 })),
      },
      promise: { value: updatePromise },
    });
    button.dispatchEvent(cartEvent);

    try {
      await updatePromise;
      if (status) status.textContent = `${button.dataset.packageName} finns nu i varukorgen.`;
    } catch (error) {
      if (status) {
        status.textContent = error.message || 'Paketet kunde inte läggas i varukorgen. Försök igen.';
      }
    } finally {
      button.classList.remove('is-loading');
      setButtonsDisabled(root, false);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest(packageButtonSelector);
      if (!button || button.disabled || button.dataset.canAdd !== 'true') return;

      const root = button.closest(packageRootSelector);
      if (!root) return;

      const variantIds = (button.dataset.variantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (!variantIds.length || variantIds.some((id) => !/^\d+$/.test(id))) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      addPackage(button, root, variantIds);
    },
    true
  );
})();

(() => {
  const feedSelector = '#insta-feed';
  const itemSelector = '.instafeed-new-layout-item';
  const excludedPost = 'a[href="https://www.instagram.com/reel/DX1Z_nytGxp/"]';
  const visiblePostCount = 12;
  const instagramProfileUrl = 'https://www.instagram.com/belyr.se/';
  const instafeedScriptSrc = 'https://cdn.nfcube.com/instafeed-47227b07e6c479df4876f50775cdc6e3.js?shop=1hp4xn-ma.myshopify.com';
  let feedObserver;

  const loadInstafeedScript = () => {
    if (document.querySelector('script[src*="cdn.nfcube.com/instafeed-"]')) return;

    const script = document.createElement('script');
    script.src = instafeedScriptSrc;
    script.async = true;
    document.head.appendChild(script);
  };

  const loadPrimaryImage = (item) => {
    const image = Array.from(item.querySelectorAll('img[data-instafeed-src]')).find((candidate) => {
      const source = candidate.dataset.instafeedSrc;
      return source && !source.includes('instafeed.nfcube.com/assets/img/');
    });

    if (!image) return;

    const source = image.dataset.instafeedSrc;
    const currentSource = image.getAttribute('src') || '';

    if (source && currentSource.includes('/assets/img/placeholder.png')) {
      image.src = source;
    }
  };

  const filterInstagramFeed = () => {
    const feed = document.querySelector(feedSelector);
    if (!feed) return false;

    const heading = feed.querySelector('h2');

    if (heading) {
      let headingLink = heading.querySelector('.belyr-instagram-heading-link');

      if (!headingLink) {
        heading.textContent = '';
        headingLink = document.createElement('a');
        headingLink.className = 'belyr-instagram-heading-link';
        heading.appendChild(headingLink);
      }

      headingLink.href = instagramProfileUrl;
      headingLink.target = '_blank';
      headingLink.rel = 'noopener noreferrer';

      if (headingLink.textContent.trim() !== 'belyr.se') {
        headingLink.textContent = 'belyr.se';
      }
    }

    const items = Array.from(feed.querySelectorAll(itemSelector));
    if (items.length <= visiblePostCount) return false;

    let visibleItems = 0;

    items.forEach((item) => {
      const shouldShow = !item.querySelector(excludedPost) && visibleItems < visiblePostCount;

      if (shouldShow) {
        item.style.removeProperty('display');
        visibleItems += 1;

        if (visibleItems === 1) loadPrimaryImage(item);
      } else {
        item.style.setProperty('display', 'none', 'important');
      }
    });

    return visibleItems === visiblePostCount;
  };

  const setup = () => {
    loadInstafeedScript();
    filterInstagramFeed();

    if (feedObserver) return;

    feedObserver = new MutationObserver(() => {
      filterInstagramFeed();
    });

    feedObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  document.addEventListener('shopify:section:load', setup);
})();
